import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Minus,
  Plus,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Trash2,
  Truck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const CART_KEY = "cart";
const FREE_DELIVERY_THRESHOLD = 4999;
const STANDARD_DELIVERY_FEE = 99;

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const getCartKey = (item, index) =>
  item.variantId ||
  `${item.id}-${item.size || ""}-${item.color || ""}-${index}`;

const readCart = () => {
  try {
    const storedCart = JSON.parse(localStorage.getItem(CART_KEY)) || [];

    if (!Array.isArray(storedCart)) return [];

    return storedCart.map((item) => ({
      ...item,
      quantity: Math.max(1, Number(item.quantity ?? item.qty ?? 1)),
      price: Number(item.price || 0),
      mrp: Number(item.mrp ?? item.price ?? 0),
    }));
  } catch {
    return [];
  }
};

const getImageUrl = (image) => {
  if (!image || typeof image !== "string") return null;

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  return `${process.env.REACT_APP_ASSET_URL || ""}${image}`;
};

export default function CartPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isCustomerLoading } = useCustomerAuth();

  const [cart, setCart] = useState(readCart);

  const saveCart = (updatedCart) => {
    setCart(updatedCart);
    localStorage.setItem(CART_KEY, JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  useEffect(() => {
    const syncCart = () => setCart(readCart());

    window.addEventListener("storage", syncCart);
    window.addEventListener("cartUpdated", syncCart);

    return () => {
      window.removeEventListener("storage", syncCart);
      window.removeEventListener("cartUpdated", syncCart);
    };
  }, []);

  const updateQuantity = (itemKey, nextQuantity) => {
    if (nextQuantity < 1) return;

    saveCart(
      cart.map((item, index) =>
        getCartKey(item, index) === itemKey
          ? {
              ...item,
              quantity:
                item.availableQuantity !== undefined &&
                item.availableQuantity !== null
                  ? Math.min(nextQuantity, Number(item.availableQuantity))
                  : nextQuantity,
            }
          : item,
      ),
    );
  };

  const removeItem = (itemKey) => {
    saveCart(
      cart.filter((item, index) => getCartKey(item, index) !== itemKey),
    );
  };

  const clearCart = () => {
    if (!window.confirm("Remove all products from your shopping bag?")) return;

    saveCart([]);
  };

  const summary = useMemo(() => {
    const mrpTotal = cart.reduce(
      (total, item) => total + Number(item.mrp || item.price) * item.quantity,
      0,
    );

    const sellingTotal = cart.reduce(
      (total, item) => total + Number(item.price) * item.quantity,
      0,
    );

    const discount = Math.max(0, mrpTotal - sellingTotal);

    const deliveryFee =
      sellingTotal === 0 || sellingTotal >= FREE_DELIVERY_THRESHOLD
        ? 0
        : STANDARD_DELIVERY_FEE;

    return {
      itemCount: cart.reduce((total, item) => total + item.quantity, 0),
      mrpTotal,
      sellingTotal,
      discount,
      deliveryFee,
      total: sellingTotal + deliveryFee,
    };
  }, [cart]);

  const amountForFreeDelivery = Math.max(
    0,
    FREE_DELIVERY_THRESHOLD - summary.sellingTotal,
  );

  const handleCheckout = () => {
    if (!cart.length || isCustomerLoading) return;

    if (!isAuthenticated) {
      navigate("/signin", {
        state: {
          from: "/checkout",
        },
      });
      return;
    }

    navigate("/checkout");
  };

  return (
    <main className="min-h-[70vh] bg-[#faf9f7]">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
        {/* Header */}
        <div className="mb-10 flex flex-col gap-5 border-b border-[#eadde1] pb-7 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate("/search/all")}
              className="mb-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500 transition hover:text-primary-800"
            >
              <ArrowLeft size={15} />
              Continue shopping
            </button>

            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary-800">
              Shopping bag
            </p>

            <h1 className="mt-2 text-4xl font-semibold tracking-[-0.045em] text-slate-950 sm:text-5xl">
              Your selection
            </h1>

            <p className="mt-3 text-sm text-slate-500 sm:text-base">
              {summary.itemCount
                ? `${summary.itemCount} item${
                    summary.itemCount > 1 ? "s" : ""
                  } ready for checkout.`
                : "Your selected products will appear here."}
            </p>
          </div>

          {cart.length > 0 ? (
            <button
              type="button"
              onClick={clearCart}
              className="text-xs font-bold uppercase tracking-[0.13em] text-red-700 transition hover:text-red-950"
            >
              Clear bag
            </button>
          ) : null}
        </div>

        {cart.length === 0 ? (
          <section className="flex min-h-[440px] flex-col items-center justify-center border border-dashed border-[#ded8d4] bg-white px-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-800">
              <ShoppingCart size={28} />
            </div>

            <h2 className="mt-6 text-2xl font-semibold tracking-tight text-slate-950">
              Your bag is empty
            </h2>

            <p className="mt-3 max-w-sm text-sm leading-6 text-slate-500">
              Explore premium fashion from CHARMS and add the styles you love.
            </p>

            <button
              type="button"
              onClick={() => navigate("/search/all")}
              className="mt-7 inline-flex items-center gap-2 bg-primary-800 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-primary-950"
            >
              Shop the collection
              <ArrowRight size={16} />
            </button>
          </section>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px]">
            {/* Bag items */}
            <section>
              <div className="border-t border-slate-300">
                {cart.map((item, index) => {
                  const itemKey = getCartKey(item, index);
                  const imageUrl = getImageUrl(item.image);
                  const itemMrp = Number(item.mrp || item.price);
                  const discount = Math.max(0, itemMrp - Number(item.price));
                  const discountPercent =
                    itemMrp > Number(item.price)
                      ? Math.round((discount / itemMrp) * 100)
                      : 0;

                  return (
                    <article
                      key={itemKey}
                      className="grid grid-cols-[104px_minmax(0,1fr)] gap-4 border-b border-slate-200 py-5 sm:grid-cols-[135px_minmax(0,1fr)] sm:gap-6 sm:py-6"
                    >
                      <button
                        type="button"
                        onClick={() => navigate("/product/" + encodeURIComponent(item.slug || item.id))}
                        className="aspect-[3/4] w-full overflow-hidden bg-slate-100"
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.title}
                            className="h-full w-full object-cover transition duration-500 hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary-100 to-[#f1dfe3] p-3 text-center text-xs font-bold text-primary-800">
                            {item.title}
                          </div>
                        )}
                      </button>

                      <div className="min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary-800">
                              {item.brand || "CHARMS"}
                            </p>

                            <button
                              type="button"
                              onClick={() => navigate("/product/" + encodeURIComponent(item.slug || item.id))}
                              className="mt-1 line-clamp-2 text-left text-base font-semibold leading-snug text-slate-950 transition hover:text-primary-800 sm:text-lg"
                            >
                              {item.title}
                            </button>
                            {item.productCode && <p className="mt-1 text-xs text-slate-500">Article No: {item.productCode}</p>}

                            {(item.color || item.size) && (
                              <p className="mt-2 text-xs text-slate-500">
                                {[item.color, item.size ? `Size ${item.size}` : ""]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                            )}

                            {item.sku ? (
                              <p className="mt-1 text-[11px] text-slate-400">
                                {item.sku}
                              </p>
                            ) : null}
                          </div>

                          <button
                            type="button"
                            onClick={() => removeItem(itemKey)}
                            className="flex h-9 w-9 shrink-0 items-center justify-center text-slate-400 transition hover:bg-red-50 hover:text-red-700"
                            aria-label={`Remove ${item.title} from shopping bag`}
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>

                        <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                          <span className="text-lg font-bold text-slate-950">
                            {formatCurrency(item.price)}
                          </span>

                          {itemMrp > Number(item.price) ? (
                            <>
                              <span className="text-xs text-slate-400 line-through">
                                {formatCurrency(itemMrp)}
                              </span>

                              <span className="text-xs font-semibold text-primary-800">
                                {discountPercent}% off
                              </span>
                            </>
                          ) : null}
                        </div>

                        <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                          <div className="flex h-10 items-center border border-slate-300 bg-white">
                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(itemKey, item.quantity - 1)
                              }
                              disabled={item.quantity <= 1}
                              className="flex h-full w-10 items-center justify-center text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-35"
                              aria-label="Decrease quantity"
                            >
                              <Minus size={15} />
                            </button>

                            <span className="flex h-full min-w-10 items-center justify-center border-x border-slate-300 px-2 text-sm font-semibold text-slate-950">
                              {item.quantity}
                            </span>

                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(itemKey, item.quantity + 1)
                              }
                              disabled={
                                item.availableQuantity !== undefined &&
                                item.availableQuantity !== null &&
                                Number(item.quantity) >=
                                  Number(item.availableQuantity)
                              }
                              className="flex h-full w-10 items-center justify-center text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-35"
                              aria-label="Increase quantity"
                            >
                              <Plus size={15} />
                            </button>
                          </div>

                          <p className="text-right text-xs text-slate-500">
                            Item total
                            <span className="ml-2 font-bold text-slate-950">
                              {formatCurrency(item.price * item.quantity)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>

            {/* Summary */}
            <aside className="h-fit border border-[#ded8d4] bg-white p-6 lg:sticky lg:top-32">
              <div className="flex items-center gap-3 border-b border-slate-200 pb-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-800">
                  <ShoppingBag size={18} />
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-slate-950">
                    Order summary
                  </h2>

                  <p className="mt-0.5 text-xs text-slate-500">
                    {summary.itemCount} item
                    {summary.itemCount > 1 ? "s" : ""} in your bag
                  </p>
                </div>
              </div>

              <div className="space-y-4 border-b border-slate-200 py-6 text-sm">
                <div className="flex justify-between gap-5">
                  <span className="text-slate-500">Total MRP</span>
                  <span className="font-medium text-slate-900">
                    {formatCurrency(summary.mrpTotal)}
                  </span>
                </div>

                {summary.discount > 0 ? (
                  <div className="flex justify-between gap-5 text-primary-800">
                    <span>Product discount</span>
                    <span className="font-medium">
                      -{formatCurrency(summary.discount)}
                    </span>
                  </div>
                ) : null}

                <div className="flex justify-between gap-5">
                  <span className="text-slate-500">Delivery</span>
                  <span
                    className={
                      summary.deliveryFee
                        ? "font-medium text-slate-900"
                        : "font-semibold text-emerald-700"
                    }
                  >
                    {summary.deliveryFee
                      ? formatCurrency(summary.deliveryFee)
                      : "Free"}
                  </span>
                </div>
              </div>

              {amountForFreeDelivery > 0 ? (
                <div className="mt-5 border border-primary-100 bg-primary-50 px-4 py-3">
                  <div className="flex items-start gap-2">
                    <Truck
                      size={16}
                      className="mt-0.5 shrink-0 text-primary-800"
                    />

                    <p className="text-xs leading-5 text-primary-900">
                      Add{" "}
                      <strong>{formatCurrency(amountForFreeDelivery)}</strong>{" "}
                      more to unlock free delivery.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mt-5 border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-700">
                  You have unlocked free delivery.
                </div>
              )}

              <div className="mt-6 flex items-end justify-between">
                <span className="text-base font-semibold text-slate-950">
                  Total amount
                </span>

                <span className="text-2xl font-semibold tracking-tight text-slate-950">
                  {formatCurrency(summary.total)}
                </span>
              </div>

              <button
                type="button"
                disabled={isCustomerLoading}
                onClick={handleCheckout}
                className="mt-6 flex w-full items-center justify-center gap-2 bg-primary-800 py-4 text-sm font-bold text-white transition hover:bg-primary-950 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCustomerLoading ? "Checking account..." : "Proceed to checkout"}
                {!isCustomerLoading ? <ArrowRight size={16} /> : null}
              </button>

              <div className="mt-5 flex items-start gap-2 border-t border-slate-100 pt-5 text-xs leading-5 text-slate-500">
                <ShieldCheck
                  size={16}
                  className="mt-0.5 shrink-0 text-primary-800"
                />

                <span>
                  Secure checkout. Apply coupons and redeem loyalty points on
                  the next step.
                </span>
              </div>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}
